import { SessionExpiredError } from "../../types";
import {
  InvitationUnavailableError,
  PermissionError,
  SignInExpiredError,
  ValidationError,
} from "../errors";
import { invitationStatus } from "../logic/invitations";
import { safeReturnTo } from "../logic/return-to";
import { sha256 } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import type { AuthMethod, IdentityApi } from "../types";
import {
  describeDevice,
  liveSession,
  requireOrg,
  requireSession,
  roleIn,
  userByEmail,
  type Ctx,
} from "./context";
import type { StoredUser } from "./state";
import { sessionView } from "./views";

/** Sign-in, the session and first run, as our identity service exposes them. Account settings: ./account.ts. */

/** The mock cannot geolocate; every new session reads as this network. */
const MOCK_NETWORK = { city: "Ahmedabad", ip: "49.36.104.27" };

type AuthApi = Pick<
  IdentityApi,
  | "startSignIn"
  | "completeSignIn"
  | "getSession"
  | "switchOrganisation"
  | "signOut"
  | "acceptTerms"
  | "completeWorkspaceChoice"
>;

/** Starts a session for `user` in this browser, ending the one it had (the cookie is replaced). */
export function openSession(
  ctx: Ctx,
  user: StoredUser,
  method: AuthMethod,
  organisationId: string | undefined,
): void {
  const now = ctx.now();
  const previous = ctx.state.sessions.find(
    (s) => s.id === ctx.state.cookie && s.endedAt === undefined,
  );
  if (previous !== undefined) {
    previous.endedAt = now;
    previous.endReason = "signed_out";
  }
  const firstMembership = ctx.state.memberships.find((m) => m.userId === user.id)?.organisationId;
  const lastUsed = [...ctx.state.sessions]
    .filter((s) => s.userId === user.id && s.organisationId !== undefined)
    .sort((a, b) => b.lastActiveAt - a.lastActiveAt)[0]?.organisationId;
  const candidates = [organisationId, lastUsed, firstMembership];
  const chosen = candidates.find(
    (id) => id !== undefined && roleIn(ctx, user.id, id) !== undefined,
  );
  const id = ctx.token();
  ctx.state.sessions.push({
    id,
    userId: user.id,
    ...(chosen === undefined ? {} : { organisationId: chosen }),
    createdAt: now,
    lastActiveAt: now,
    authMethod: method,
    ...describeDevice(ctx.options.userAgent),
    ...MOCK_NETWORK,
  });
  ctx.state.cookie = id;
}

export function createAuthApi(ctx: Ctx): AuthApi {
  const view = () => {
    const { session, user } = requireSession(ctx);
    return sessionView(ctx, session, user);
  };

  return {
    async startSignIn(returnTo, options = {}) {
      await ctx.wait();
      const now = ctx.now();
      let invitationId: string | undefined;
      let screen = options.screen ?? "sign-in";
      if (options.invitationToken !== undefined) {
        const hash = await sha256(options.invitationToken);
        const invitation = ctx.state.invitations.find((inv) => inv.tokenHash === hash);
        if (invitation === undefined) throw new InvitationUnavailableError("no longer valid");
        const status = invitationStatus(invitation, now);
        if (status !== "pending") throw new InvitationUnavailableError(status);
        invitationId = invitation.id;
        // AuthKit shows sign-up to new people and sign-in to existing accounts.
        screen = userByEmail(ctx, invitation.email) === undefined ? "sign-up" : "sign-in";
      }
      const state = ctx.token();
      ctx.state.signIns = ctx.state.signIns.filter((s) => s.expiresAt > now);
      ctx.state.signIns.push({
        state,
        screen,
        returnTo: safeReturnTo(returnTo),
        ...(invitationId === undefined ? {} : { invitationId }),
        expiresAt: now + IDENTITY_POLICY.signInRequestMs,
      });
      ctx.persist();
      // Live: GET /auth/sign-in?return_to=… answers 302 to the AuthKit hosted page with this state.
      return `/${screen}?state=${encodeURIComponent(state)}`;
    },

    async completeSignIn(code, state) {
      await ctx.wait();
      const now = ctx.now();
      const signIn = ctx.state.signIns.find((s) => s.state === state && s.expiresAt > now);
      const codeHash = await sha256(code);
      const done = ctx.state.authenticated.find(
        (a) => a.state === state && a.callbackCodeHash === codeHash && a.expiresAt > now,
      );
      const user =
        done === undefined ? undefined : ctx.state.users.find((u) => u.id === done.userId);
      if (signIn === undefined || done === undefined || user === undefined)
        throw new SignInExpiredError();
      // Single use: the state, its codes and the callback code are consumed.
      ctx.state.signIns = ctx.state.signIns.filter((s) => s.state !== state);
      ctx.state.authenticated = ctx.state.authenticated.filter((a) => a.state !== state);
      ctx.state.magicCodes = ctx.state.magicCodes.filter((c) => c.state !== state);
      let organisationId: string | undefined;
      const invitation = ctx.state.invitations.find((inv) => inv.id === signIn.invitationId);
      if (
        invitation !== undefined &&
        invitationStatus(invitation, now) === "pending" &&
        invitation.email === user.email
      ) {
        if (roleIn(ctx, user.id, invitation.organisationId) === undefined) {
          ctx.state.memberships.push({
            userId: user.id,
            organisationId: invitation.organisationId,
            role: invitation.role,
            joinedAt: now,
          });
        }
        invitation.status = "accepted";
        organisationId = invitation.organisationId;
      }
      openSession(ctx, user, done.method, organisationId);
      ctx.commit();
      return { session: view(), returnTo: signIn.returnTo };
    },

    async getSession() {
      await ctx.wait();
      const live = liveSession(ctx);
      if (live === "none") return null;
      if (live === "expired") throw new SessionExpiredError();
      return sessionView(ctx, live.session, live.user);
    },

    async switchOrganisation(organisationId) {
      await ctx.wait();
      const { session, user } = requireSession(ctx);
      if (roleIn(ctx, user.id, organisationId) === undefined)
        throw new PermissionError("You aren't a member of that organisation.");
      session.organisationId = organisationId;
      ctx.commit();
      return sessionView(ctx, session, user);
    },

    async signOut() {
      await ctx.wait();
      const session = ctx.state.sessions.find((s) => s.id === ctx.state.cookie);
      if (session !== undefined && session.endedAt === undefined) {
        session.endedAt = ctx.now();
        session.endReason = "signed_out";
      }
      delete ctx.state.cookie;
      ctx.commit();
    },

    async acceptTerms(version, ageConfirmed) {
      await ctx.wait();
      const { user } = requireSession(ctx);
      if (!ageConfirmed)
        throw new ValidationError(
          "age",
          `Confirm that you are ${String(IDENTITY_POLICY.minimumAge)} or older to use Orthos.`,
        );
      if (version !== IDENTITY_POLICY.termsVersion)
        throw new ValidationError(
          "terms",
          "The terms have changed. Reload the page to read the current version.",
        );
      user.termsVersion = version;
      user.termsAcceptedAt = ctx.now();
      user.ageConfirmedAt = ctx.now();
      ctx.commit();
      return view();
    },

    async completeWorkspaceChoice() {
      await ctx.wait();
      requireSession(ctx).user.workspaceChosen = true;
      ctx.commit();
      return view();
    },
  };
}

/** Used by the organisation and onboarding parts. */
export function currentOrgView(ctx: Ctx) {
  const { session, user } = requireOrg(ctx);
  return sessionView(ctx, session, user);
}
